import { InvalidVizConfig, VizConfig } from "@/shared/config";
import { ConfigService } from "@/shared/domain/config";
import * as GetUserConfig from "@/shared/domain/config/hooks/useUserConfig";
import {
  LayoutDefinition,
  LayoutRecord,
  LayoutService,
  InvalidLayoutDefinitionError,
  UnsupportedLayoutSchemaVersion,
  createEmptyLayout,
} from "@/shared/domain/layouts";
import { layoutQueryKeys } from "@/shared/domain/layouts/hooks";
import { WorkspaceService } from "@/shared/domain/workspaces";
import { RobotoNotFoundException } from "@/shared/http";
import {
  RobotoLayoutConflictException,
  RobotoUnauthorizedException,
} from "@/shared/http/exceptions";
import { LoggerService } from "@/shared/services/LoggerService";

import {
  InvalidWorkspaceConfigError,
  WorkspaceNotFoundError,
} from "./exceptions";
import type {
  VisualizerInitContext,
  VisualizerInitData,
} from "./visualizerInit";

type Resolver = (ctx: VisualizerInitContext) => Promise<VisualizerInitData>;

/** A list of resolvers sorted by priority */
export const visualizerResolvers: Resolver[] = [
  resolveByWorkspaceId,
  resolveByLayoutName,
  resolveByLayoutFromNavigationState,
  resolveByDefaultLayout,
];

/**
 * Resolve the visualizer state from a shared workspace link.
 *
 * @returns {VisualizerInitData}
 * @throws {UnrecoverableError} if an expected unrecoverable error occurs
 * @throws {Error} for unexpected errors
 */
async function resolveByWorkspaceId(
  ctx: VisualizerInitContext,
): Promise<VisualizerInitData> {
  const { workspaceId, orgId } = ctx;

  LoggerService.log("Attempting to resolve by workspace id", workspaceId);

  if (!workspaceId) {
    return null;
  }

  const workspaceService = new WorkspaceService(ctx.httpClient);

  const getWorkspaceQuery = () => {
    return ctx.queryClient.ensureQueryData({
      queryKey: ["workspace", workspaceId],
      queryFn: ({ signal }) =>
        workspaceService.getWorkspace(orgId, workspaceId, {
          abortSignal: signal,
        }),
    });
  };

  try {
    const workspace = await getWorkspaceQuery();
    const vizConfig = VizConfig.from_obj(workspace.config);
    return { type: "workspace", value: vizConfig.toObject() };
  } catch (error) {
    // Expected errors
    if (error instanceof RobotoUnauthorizedException) {
      throw error;
    }
    if (error instanceof RobotoNotFoundException) {
      throw new WorkspaceNotFoundError(workspaceId, { cause: error });
    }
    if (error instanceof InvalidVizConfig) {
      throw new InvalidWorkspaceConfigError(workspaceId, { cause: error });
    }

    // Unexpected error
    throw new Error(`Failed to resolve workspace ${workspaceId}`, {
      cause: error,
    });
  }
}

async function resolveByLayoutName(
  ctx: VisualizerInitContext,
): Promise<VisualizerInitData> {
  const { layoutName, orgId } = ctx;

  LoggerService.log("Attempting to resolve by layout name", layoutName);

  if (!layoutName) {
    return null;
  }

  const layoutService = new LayoutService(ctx.httpClient);

  const getLayoutByNameQuery = () => {
    return ctx.queryClient.ensureQueryData({
      queryKey: layoutQueryKeys.name(layoutName),
      queryFn: ({ signal }) =>
        layoutService.getLayoutByName(layoutName, orgId, {
          abortSignal: signal,
        }),
    });
  };

  try {
    const layout = await getLayoutByNameQuery();
    return { type: "layout", value: layout };
  } catch (error) {
    // Expected errors
    if (error instanceof RobotoUnauthorizedException) {
      throw error;
    }
    if (error instanceof RobotoNotFoundException) {
      return {
        type: "recoverable-error",
        value: {
          name: "LayoutNotFoundError",
          message: `Layout "${layoutName}" not found`,
        },
      };
    }

    if (error instanceof RobotoLayoutConflictException) {
      return {
        type: "recoverable-error",
        value: {
          name: "LayoutConflictError",
          message: `Multiple layouts named "${layoutName}" found`,
          metadata: { conflictingLayouts: error.conflictingLayouts },
        },
      };
    }

    // Unexpected error
    throw new Error(`Failed to resolve layout ${layoutName}`, {
      cause: error,
    });
  }
}

function resolveByLayoutFromNavigationState(
  ctx: VisualizerInitContext,
): Promise<VisualizerInitData> {
  const { layoutFromNavState } = ctx;

  LoggerService.log(
    "Attempting to resolve by layout definition from navigation state",
    layoutFromNavState,
  );

  if (!layoutFromNavState) {
    return Promise.resolve(null);
  }

  try {
    const layoutDefinition = LayoutDefinition.from_obj(
      layoutFromNavState.layoutDefinition,
      layoutFromNavState.schemaVersion,
    );

    const layout: LayoutRecord = {
      ...createEmptyLayout(),
      layout_definition: layoutDefinition.state,
      schema_version: layoutDefinition.schemaVersion,
    };

    return Promise.resolve({ type: "layout", value: layout });
  } catch (error) {
    // Expected errors
    if (error instanceof InvalidLayoutDefinitionError) {
      // This is a programming error
      return Promise.reject(error);
    }
    if (error instanceof UnsupportedLayoutSchemaVersion) {
      // This is a programming error
      return Promise.reject(error);
    }

    // Unexpected error
    return Promise.reject(
      new Error("Failed to resolve layout definition from navigation state", {
        cause: error,
      }),
    );
  }
}

async function resolveByDefaultLayout(
  ctx: VisualizerInitContext,
): Promise<VisualizerInitData> {
  LoggerService.log("Attempting to resolve by default layout ID for user");

  const { orgId } = ctx;

  const configService = new ConfigService(ctx.httpClient);

  const getUserDefaultLayoutQuery = () => {
    return ctx.queryClient.ensureQueryData({
      queryKey: GetUserConfig.getQueryKey("user_default_layout_id"),
      queryFn: ({ signal }) =>
        configService.getUserConfig<string>("user_default_layout_id", {
          abortSignal: signal,
        }),
    });
  };

  try {
    // Get the user's default layout ID from config
    const userDefaultLayoutId = await getUserDefaultLayoutQuery();

    if (!userDefaultLayoutId) {
      return null;
    }

    // Then fetch the layout by ID
    const layoutService = new LayoutService(ctx.httpClient);

    const getLayoutByIdQuery = () => {
      return ctx.queryClient.ensureQueryData({
        queryKey: layoutQueryKeys.id(userDefaultLayoutId),
        queryFn: ({ signal }) =>
          layoutService.getLayoutById(userDefaultLayoutId, orgId, {
            abortSignal: signal,
          }),
      });
    };

    try {
      const layout = await getLayoutByIdQuery();
      return { type: "layout", value: layout };
    } catch (error) {
      // Expected errors
      if (error instanceof RobotoNotFoundException) {
        // User's default layout was deleted. Clear the config (non-blocking).
        void configService.setUserConfig("user_default_layout_id", "");

        // Then return a recoverable error
        return {
          type: "recoverable-error",
          value: {
            name: "DefaultLayoutNotFoundError",
            message: "Your default layout was not found",
          },
        };
      }

      if (error instanceof RobotoUnauthorizedException) {
        return {
          type: "recoverable-error",
          value: {
            name: "UnauthorizedError",
            message:
              "You are no longer authorized to view your default layout. Check with the layout owner.",
          },
        };
      }

      throw error;
    }
  } catch (error) {
    // Expected errors
    if (error instanceof RobotoUnauthorizedException) {
      throw error;
    }

    // Unexpected error
    throw new Error("Failed to get user's default layout from config", {
      cause: error,
    });
  }
}
